import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, Loader2, Sparkles, Crown, Rocket, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const ICONS = { free: Sparkles, pro: Rocket, enterprise: Crown };

const UsageBar = ({ label, used, limit }) => {
  const unlimited = limit < 0;
  const pct = unlimited ? 8 : Math.min(100, Math.round((used / Math.max(1, limit)) * 100));
  const near = !unlimited && pct >= 80;
  return (
    <div data-testid={`usage-${label}`}>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-muted-foreground capitalize">{label.replace('_', ' ')}</span>
        <span className="font-medium">{used}{unlimited ? '' : ` / ${limit}`}</span>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div className={`h-full rounded-full ${near ? 'bg-amber-500' : 'bg-primary'}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export default function BillingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [txns, setTxns] = useState([]);
  const [cycle, setCycle] = useState('monthly');
  const [busy, setBusy] = useState('');
  const [polling, setPolling] = useState(false);
  const [changePreview, setChangePreview] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, s, t] = await Promise.all([api.getBillingPlans(), api.getSubscription(), api.getBillingTransactions()]);
      setPlans(p.plans);
      setSub(s);
      setTxns(t.transactions || []);
      setCycle(s.cycle === 'annual' ? 'annual' : 'monthly');
    } catch (e) { toast.error(e.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Handle return from Stripe (/billing/success?session_id=...)
  useEffect(() => {
    const sid = new URLSearchParams(location.search).get('session_id');
    if (!sid) return;
    setPolling(true);
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const st = await api.getCheckoutStatus(sid);
        if (st.payment_status === 'paid') {
          toast.success('Payment successful — your plan is now active!');
          setPolling(false);
          await load();
          navigate('/billing', { replace: true });
          return;
        }
        if (st.status === 'expired' || attempts >= 8) {
          toast.error(attempts >= 8 ? 'Still processing — check back shortly.' : 'Payment session expired.');
          setPolling(false);
          navigate('/billing', { replace: true });
          return;
        }
      } catch (e) { /* keep polling */ }
      setTimeout(poll, 2000);
    };
    poll();
  }, [location.search, load, navigate]);

  const upgrade = async (planId) => {
    setBusy(planId);
    try {
      const { url } = await api.createCheckout(planId, cycle, window.location.origin);
      window.location.href = url;
    } catch (e) {
      toast.error(e.message);
      setBusy('');
    }
  };

  const startPlanChange = async (planId) => {
    setBusy(planId);
    try {
      const preview = await api.previewPlanChange(planId, cycle);
      setChangePreview(preview);
    } catch (e) { toast.error(e.message); }
    setBusy('');
  };

  const confirmPlanChange = async () => {
    if (!changePreview) return;
    setBusy('change');
    try {
      const r = await api.changePlan(changePreview.plan_id, changePreview.cycle);
      toast.success(r.message || 'Plan updated');
      setChangePreview(null);
      await load();
    } catch (e) { toast.error(e.message); }
    setBusy('');
  };

  const cancel = async () => {
    if (!window.confirm('Cancel your subscription? You keep access until the end of the current billing period, then revert to Free.')) return;
    setBusy('cancel');
    try {
      const r = await api.cancelSubscription();
      toast.success(r.message || 'Subscription canceled');
      await load();
    } catch (e) { toast.error(e.message); }
    setBusy('');
  };

  const resume = async () => {
    setBusy('resume');
    try {
      const r = await api.resumeSubscription();
      toast.success(r.message || 'Subscription resumed');
      await load();
    } catch (e) { toast.error(e.message); }
    setBusy('');
  };

  const priceFor = (p) => {
    if (p.price_per_user === 0) return '$0';
    if (cycle === 'annual') return `$${Math.round(p.price_per_user * 12 * (1 - (p.annual_discount || 0)))}`;
    return `$${p.price_per_user}`;
  };
  const priceSuffix = cycle === 'annual' ? '/user/yr' : '/user/mo';

  if (polling) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-4" data-testid="billing-polling">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Confirming your payment…</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="billing-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-6" data-testid="billing-back">
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>

      <div className="mb-10">
        <h1 className="text-3xl font-bold tracking-tight">Billing &amp; Plans</h1>
        <p className="text-muted-foreground mt-1 text-sm">Manage your team's subscription and usage.</p>
      </div>

      {sub && (
        <div className="rounded-xl border bg-card p-6 mb-10" data-testid="current-plan-card">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Current plan</p>
              <p className="text-2xl font-semibold flex items-center gap-2" data-testid="current-plan-name">
                {sub.plan_name}
                <span className="text-xs font-normal text-muted-foreground">({sub.status})</span>
              </p>
            </div>
            {sub.current_period_end && (
              <p className="text-xs text-muted-foreground" data-testid="renewal-info">
                {sub.cancel_at
                  ? `Ends ${new Date(sub.current_period_end).toLocaleDateString()}`
                  : sub.auto_renew
                    ? `Auto-renews ${new Date(sub.current_period_end).toLocaleDateString()}`
                    : `Renews ${new Date(sub.current_period_end).toLocaleDateString()}`}
              </p>
            )}
          </div>
          {sub.status === 'past_due' && (
            <div className="mb-4 text-xs rounded-md bg-rose-500/10 text-rose-600 dark:text-rose-400 px-3 py-2" data-testid="past-due-banner">
              Your last payment failed. Stripe will retry automatically — please make sure your card is valid to keep {sub.plan_name} access.
            </div>
          )}
          {sub.cancel_at && (
            <div className="mb-4 text-xs rounded-md bg-amber-500/10 text-amber-600 dark:text-amber-400 px-3 py-2" data-testid="cancel-banner">
              Auto-renewal is off — your subscription ends on {new Date(sub.cancel_at).toLocaleDateString()}. You keep {sub.plan_name} access until then.
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <UsageBar label="semantic_models" used={sub.usage.semantic_models} limit={sub.entitlements.limits.semantic_models} />
            <UsageBar label="dashboards" used={sub.usage.dashboards} limit={sub.entitlements.limits.dashboards} />
            <UsageBar label="seats" used={sub.usage.seats} limit={sub.entitlements.limits.seats} />
          </div>
          {sub.auto_renew && (
            <p className="text-[11px] text-muted-foreground mt-3" data-testid="seat-sync-note">
              Billed seats: {sub.billed_seats ?? sub.usage.seats} — billing auto-syncs with your team size (prorated on your next invoice).
            </p>
          )}
          {sub.is_admin && sub.plan !== 'free' && (sub.cancel_at || sub.auto_renew) && (
            <div className="mt-6 pt-4 border-t flex justify-end">
              {sub.cancel_at ? (
                <Button variant="outline" size="sm" disabled={!!busy} onClick={resume} data-testid="resume-subscription-btn">
                  {busy === 'resume' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Resume subscription'}
                </Button>
              ) : (
                <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" disabled={!!busy} onClick={cancel} data-testid="cancel-subscription-btn">
                  {busy === 'cancel' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cancel auto-renewal'}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-center gap-3 mb-8">
        <span className={`text-sm ${cycle === 'monthly' ? 'font-semibold' : 'text-muted-foreground'}`}>Monthly</span>
        <button
          onClick={() => setCycle(cycle === 'monthly' ? 'annual' : 'monthly')}
          className="relative w-12 h-6 rounded-full bg-muted transition-colors"
          data-testid="billing-cycle-toggle"
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-primary transition-transform ${cycle === 'annual' ? 'translate-x-6' : ''}`} />
        </button>
        <span className={`text-sm ${cycle === 'annual' ? 'font-semibold' : 'text-muted-foreground'}`}>
          Annual <span className="text-emerald-500 text-xs">(save 20%)</span>
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((p) => {
          const Icon = ICONS[p.id] || Sparkles;
          const isCurrent = sub && sub.plan === p.id;
          const isCurrentExact = isCurrent && sub.cycle === cycle;
          const canManage = sub && sub.is_admin;
          const paid = p.price_per_user > 0;
          const canSwitch = canManage && sub.auto_renew && sub.plan !== 'free' && paid && !isCurrentExact;
          return (
            <div
              key={p.id}
              className={`rounded-xl border p-6 flex flex-col ${p.id === 'pro' ? 'border-primary shadow-lg ring-1 ring-primary/20' : ''}`}
              data-testid={`plan-card-${p.id}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-semibold">{p.name}</h3>
                {p.id === 'pro' && <span className="ml-auto text-[10px] uppercase tracking-wide bg-primary text-primary-foreground px-2 py-0.5 rounded-full">Popular</span>}
              </div>
              <p className="text-xs text-muted-foreground mb-4 h-8">{p.tagline}</p>
              <div className="mb-5">
                {paid && cycle === 'annual' && p.annual_discount > 0 && (
                  <span className="text-base text-muted-foreground line-through mr-2" data-testid={`plan-full-price-${p.id}`}>
                    ${(p.price_per_user * 12).toLocaleString()}
                  </span>
                )}
                <span className="text-3xl font-bold">{priceFor(p)}</span>
                {paid && <span className="text-sm text-muted-foreground">{priceSuffix}</span>}
                {paid ? (
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {cycle === 'annual' && p.annual_discount > 0 ? (
                      <span className="text-emerald-500 font-medium" data-testid={`plan-discount-${p.id}`}>
                        Save {Math.round(p.annual_discount * 100)}% — billed annually, auto-renews
                      </span>
                    ) : 'Billed monthly, auto-renews'}
                  </p>
                ) : null}
              </div>
              <ul className="space-y-2 mb-6 flex-1">
                {p.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {h}
                  </li>
                ))}
              </ul>
              {isCurrentExact ? (
                <Button disabled variant="secondary" className="w-full" data-testid={`plan-current-${p.id}`}>Current plan</Button>
              ) : !paid ? (
                <Button disabled variant="outline" className="w-full">—</Button>
              ) : canSwitch ? (
                <Button
                  variant={isCurrent ? 'outline' : 'default'}
                  className="w-full"
                  disabled={!!busy}
                  onClick={() => startPlanChange(p.id)}
                  data-testid={`plan-switch-${p.id}`}
                >
                  {busy === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> :
                    isCurrent ? `Switch to ${cycle}` : `Switch to ${p.name}`}
                </Button>
              ) : (
                <Button
                  className="w-full"
                  disabled={!canManage || !!busy}
                  onClick={() => upgrade(p.id)}
                  data-testid={`plan-upgrade-${p.id}`}
                  title={!canManage ? 'Only tenant admins can manage billing' : ''}
                >
                  {busy === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : `Subscribe to ${p.name}`}
                </Button>
              )}
              {!canManage && paid && !isCurrent && (
                <p className="text-[10px] text-muted-foreground text-center mt-2">Only tenant admins can upgrade</p>
              )}
            </div>
          );
        })}
      </div>

      {changePreview && (
        <Dialog open onOpenChange={(o) => !o && setChangePreview(null)}>
          <DialogContent data-testid="change-plan-dialog">
            <DialogHeader>
              <DialogTitle>Switch to {changePreview.plan_name} ({changePreview.cycle})</DialogTitle>
              <DialogDescription>
                Your subscription is updated immediately and the difference is prorated for the rest of the current billing period.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 text-sm py-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">New recurring price</span>
                <span className="font-medium" data-testid="change-recurring-amount">
                  ${Number(changePreview.recurring_amount).toLocaleString()}/{changePreview.interval} ({changePreview.seats} seats)
                </span>
              </div>
              {changePreview.due_now > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Prorated charge today</span>
                  <span className="font-semibold" data-testid="change-due-now">${Number(changePreview.due_now).toLocaleString()}</span>
                </div>
              )}
              {changePreview.credit > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Credit applied to future invoices</span>
                  <span className="font-semibold text-emerald-500" data-testid="change-credit">${Number(changePreview.credit).toLocaleString()}</span>
                </div>
              )}
              {changePreview.due_now === 0 && changePreview.credit === 0 && (
                <p className="text-xs text-muted-foreground">No prorated amount is due today.</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setChangePreview(null)} disabled={busy === 'change'} data-testid="change-plan-cancel">
                Keep current plan
              </Button>
              <Button onClick={confirmPlanChange} disabled={busy === 'change'} data-testid="change-plan-confirm">
                {busy === 'change' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm switch'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}


      {txns.length > 0 && (
        <div className="mt-12" data-testid="billing-history">
          <h2 className="text-lg font-semibold mb-4">Billing history</h2>
          <div className="rounded-xl border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs text-muted-foreground">
                <tr>
                  <th className="text-left font-medium px-4 py-2">Date</th>
                  <th className="text-left font-medium px-4 py-2">Plan</th>
                  <th className="text-left font-medium px-4 py-2">Cycle</th>
                  <th className="text-right font-medium px-4 py-2">Amount</th>
                  <th className="text-left font-medium px-4 py-2 pl-6">Status</th>
                </tr>
              </thead>
              <tbody>
                {txns.map((t) => (
                  <tr key={t.session_id} className="border-t" data-testid="billing-history-row">
                    <td className="px-4 py-2.5">{t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}</td>
                    <td className="px-4 py-2.5 capitalize">
                      {t.plan_id}
                      {t.kind === 'renewal' && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-600 dark:text-blue-400 uppercase tracking-wide">Renewal</span>
                      )}
                      {t.kind === 'plan_change' && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-600 dark:text-violet-400 uppercase tracking-wide">Plan change</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 capitalize">{t.cycle}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">${Number(t.amount || 0).toLocaleString()}</td>
                    <td className="px-4 py-2.5 pl-6">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${t.payment_status === 'paid' ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' : 'bg-muted text-muted-foreground'}`}>
                        {t.payment_status || t.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
